import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
 getFirestore } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebaseConfig";

const db = getFirestore(app);
const functions = getFunctions(app, 'europe-west1');

// Collections
const LESSON_TYPES = "lessonTypes";
const WEEKLY_SCHEDULE = "weeklySchedule";
const SCHEDULE_EXCEPTIONS = "scheduleExceptions";
const BOOKINGS = "bookings";
const USERS = "users";
const CARNETS = "carnets";

export const bookingService = {
  // Lesson Types Management
  async createLessonType(lessonType) {
    try {
      const docRef = await addDoc(collection(db, LESSON_TYPES), {
        ...lessonType,
        createdAt: Timestamp.now(),
        active: true,
      });
      return { id: docRef.id, ...lessonType };
    } catch (error) {
      console.error("Error creating lesson type:", error);
      throw error;
    }
  },

  async getLessonTypes() {
    try {
      const q = query(
        collection(db, LESSON_TYPES),
        where("active", "==", true),
        orderBy("name")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting lesson types:", error);
      throw error;
    }
  },

  async updateLessonType(id, updates) {
    try {
      const docRef = doc(db, LESSON_TYPES, id);
      await updateDoc(docRef, updates);
      return { id, ...updates };
    } catch (error) {
      console.error("Error updating lesson type:", error);
      throw error;
    }
  },

  async deleteLessonType(id) {
    try {
      const docRef = doc(db, LESSON_TYPES, id);
      await updateDoc(docRef, { active: false });
    } catch (error) {
      console.error("Error deleting lesson type:", error);
      throw error;
    }
  },

  // Weekly Schedule Management
  async createWeeklySlot(slot) {
    try {
      const docRef = await addDoc(collection(db, WEEKLY_SCHEDULE), {
        ...slot,
        createdAt: Timestamp.now(),
        active: true,
      });
      return { id: docRef.id, ...slot };
    } catch (error) {
      console.error("Error creating weekly slot:", error);
      throw error;
    }
  },

  async getWeeklySchedule() {
    try {
      const q = query(
        collection(db, WEEKLY_SCHEDULE),
        where("active", "==", true),
        orderBy("dayOfWeek"),
        orderBy("startTime")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting weekly schedule:", error);
      throw error;
    }
  },

  async updateWeeklySlot(id, updates) {
    try {
      const docRef = doc(db, WEEKLY_SCHEDULE, id);
      await updateDoc(docRef, updates);
      return { id, ...updates };
    } catch (error) {
      console.error("Error updating weekly slot:", error);
      throw error;
    }
  },

  async deleteWeeklySlot(id) {
    try {
      const docRef = doc(db, WEEKLY_SCHEDULE, id);
      await updateDoc(docRef, { active: false });
    } catch (error) {
      console.error("Error deleting weekly slot:", error);
      throw error;
    }
  },

  async getWeeklySlotsByLessonType(lessonTypeId) {
    try {
      const q = query(
        collection(db, WEEKLY_SCHEDULE),
        where("active", "==", true),
        where("lessonTypeId", "==", lessonTypeId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting weekly slots by lesson type:", error);
      throw error;
    }
  },

  async deleteWeeklySlotsByLessonType(lessonTypeId) {
    try {
      const slots = await this.getWeeklySlotsByLessonType(lessonTypeId);
      const deletePromises = slots.map((slot) =>
        updateDoc(doc(db, WEEKLY_SCHEDULE, slot.id), { active: false })
      );
      await Promise.all(deletePromises);
      return slots.length;
    } catch (error) {
      console.error("Error deleting weekly slots by lesson type:", error);
      throw error;
    }
  },

  // Schedule Exceptions Management
  async createScheduleException(exception) {
    try {
      const docRef = await addDoc(collection(db, SCHEDULE_EXCEPTIONS), {
        ...exception,
        createdAt: Timestamp.now(),
      });
      return { id: docRef.id, ...exception };
    } catch (error) {
      console.error("Error creating schedule exception:", error);
      throw error;
    }
  },

  async getScheduleExceptions(startDate, endDate) {
    try {
      const q = query(
        collection(db, SCHEDULE_EXCEPTIONS),
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting schedule exceptions:", error);
      throw error;
    }
  },

  // Bookings Management
  async createBooking(booking) {
    try {
      // Normalize date to midnight local time so availability matching works correctly
      const normalizedDate = new Date(booking.date);
      normalizedDate.setHours(0, 0, 0, 0);

      const docRef = await addDoc(collection(db, BOOKINGS), {
        ...booking,
        date: Timestamp.fromDate(normalizedDate),
        status: "pending",
        createdAt: Timestamp.now(),
      });

      // Send notification email to admin
      try {
        const sendBookingNotification = httpsCallable(functions, "sendBookingNotification");
        await sendBookingNotification({ bookingId: docRef.id });
        console.log("Admin notification email sent successfully");
      } catch (emailError) {
        console.error("Error sending admin notification email:", emailError);
        // Non blocchiamo la prenotazione se l'email fallisce
      }

      return { id: docRef.id, ...booking };
    } catch (error) {
      console.error("Error creating booking:", error);
      throw error;
    }
  },

  async getBookings(filters = {}) {
    try {
      let q = collection(db, BOOKINGS);

      if (filters.date) {
        q = query(q, where("date", "==", filters.date));
      }

      if (filters.status) {
        q = query(q, where("status", "==", filters.status));
      }

      if (filters.userEmail) {
        q = query(q, where("userEmail", "==", filters.userEmail));
      }

      q = query(q, orderBy("date", "desc"), orderBy("startTime"));

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting bookings:", error);
      throw error;
    }
  },

  async getBookingById(id) {
    try {
      const docRef = doc(db, BOOKINGS, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error("Booking not found");
      }
    } catch (error) {
      console.error("Error getting booking:", error);
      throw error;
    }
  },

  async updateBooking(id, updates) {
    try {
      const docRef = doc(db, BOOKINGS, id);
      const updateData = { ...updates };

      if (updates.status === "confirmed" && !updates.confirmedAt) {
        updateData.confirmedAt = Timestamp.now();
      }

      if (updates.status === "cancelled" && !updates.cancelledAt) {
        updateData.cancelledAt = Timestamp.now();
      }

      await updateDoc(docRef, updateData);
      return { id, ...updateData };
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    }
  },

  async cancelBooking(id, reason) {
    try {
      return await this.updateBooking(id, {
        status: "cancelled",
        cancelledAt: Timestamp.now(),
        cancellationReason: reason,
      });
    } catch (error) {
      console.error("Error cancelling booking:", error);
      throw error;
    }
  },

  // Availability Check
  async checkSlotAvailability(date, startTime, endTime, lessonTypeId) {
    try {
      const dateTimestamp = Timestamp.fromDate(date);
      const q = query(
        collection(db, BOOKINGS),
        where("date", "==", dateTimestamp),
        where("startTime", "==", startTime),
        where("lessonTypeId", "==", lessonTypeId),
        where("status", "in", ["pending", "confirmed"])
      );

      const querySnapshot = await getDocs(q);
      const existingBookings = querySnapshot.docs.map((doc) => doc.data());

      // Get lesson type to check max participants
      const lessonTypeDoc = await getDoc(doc(db, LESSON_TYPES, lessonTypeId));
      const lessonType = lessonTypeDoc.data();

      return existingBookings.length < (lessonType?.maxParticipants || 1);
    } catch (error) {
      console.error("Error checking slot availability:", error);
      throw error;
    }
  },

  // Get available slots for a date range
  async getAvailableSlots(startDate, endDate) {
    try {
      // Get all lesson types once at the beginning
      const lessonTypes = await this.getLessonTypes();
      const lessonTypesMap = new Map(lessonTypes.map(lt => [lt.id, lt]));

      // Get weekly schedule
      const weeklySchedule = await this.getWeeklySchedule();

      // Get exceptions for the date range
      const exceptions = await this.getScheduleExceptions(
        Timestamp.fromDate(startDate),
        Timestamp.fromDate(endDate)
      );

      // Normalize date range to midnight for consistent Firestore queries
      const queryStart = new Date(startDate);
      queryStart.setHours(0, 0, 0, 0);
      const queryEnd = new Date(endDate);
      queryEnd.setHours(23, 59, 59, 999);

      // Get existing bookings for the date range
      const bookingsQuery = query(
        collection(db, BOOKINGS),
        where("date", ">=", Timestamp.fromDate(queryStart)),
        where("date", "<=", Timestamp.fromDate(queryEnd)),
        where("status", "in", ["pending", "confirmed"])
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const existingBookings = bookingsSnapshot.docs.map((doc) => doc.data());

      // Helper to get local date string (YYYY-MM-DD) without UTC conversion issues
      const toLocalDateString = (date) => {
        const d = date instanceof Date ? date : date.toDate();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Generate available slots
      const availableSlots = [];
      const currentDate = new Date(startDate);
      currentDate.setHours(0, 0, 0, 0);

      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        const dateString = toLocalDateString(currentDate);

        // Check if there's an exception for this date
        const dayException = exceptions.find(
          (exc) => toLocalDateString(exc.date) === dateString
        );

        if (!dayException || dayException.type !== "closed") {
          // Get weekly slots for this day
          const daySlots = weeklySchedule.filter(
            (slot) => slot.dayOfWeek === dayOfWeek
          );

          for (const slot of daySlots) {
            // Get lesson type from the map (no Firestore call!)
            const lessonType = lessonTypesMap.get(slot.lessonTypeId) || null;

            // Check existing bookings for this slot
            const slotBookings = existingBookings.filter(
              (booking) =>
                toLocalDateString(booking.date) === dateString &&
                booking.startTime === slot.startTime &&
                booking.lessonTypeId === slot.lessonTypeId
            );

            // Always show slots, but mark availability
            const availableSpots = Math.max(0, slot.maxBookings - slotBookings.length);

            // Check if bookable: both slot and lesson type must allow bookings
            const slotAllowsBooking = slot.maxBookings > 0 && slotBookings.length < slot.maxBookings;
            const lessonTypeAllowsBooking = !lessonType || lessonType.maxParticipants > 0;
            const isBookable = slotAllowsBooking && lessonTypeAllowsBooking;

            availableSlots.push({
              date: new Date(currentDate),
              ...slot,
              availableSpots,
              isBookable,
              lessonType,
            });
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return availableSlots;
    } catch (error) {
      console.error("Error getting available slots:", error);
      throw error;
    }
  },

  // User Management (optional)
  async createOrUpdateUser(userData) {
    try {
      const q = query(
        collection(db, USERS),
        where("email", "==", userData.email)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Create new user
        const docRef = await addDoc(collection(db, USERS), {
          ...userData,
          createdAt: Timestamp.now(),
          bookingsHistory: [],
        });
        return { id: docRef.id, ...userData };
      } else {
        // Update existing user
        const existingUser = querySnapshot.docs[0];
        await updateDoc(existingUser.ref, userData);
        return { id: existingUser.id, ...userData };
      }
    } catch (error) {
      console.error("Error creating/updating user:", error);
      throw error;
    }
  },

  // Carnets Management
  async createCarnet(carnet) {
    try {
      const docRef = await addDoc(collection(db, CARNETS), {
        ...carnet,
        createdAt: Timestamp.now(),
        active: true,
      });
      return { id: docRef.id, ...carnet };
    } catch (error) {
      console.error("Error creating carnet:", error);
      throw error;
    }
  },

  async getCarnets() {
    try {
      const q = query(
        collection(db, CARNETS),
        where("active", "==", true),
        orderBy("name")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting carnets:", error);
      throw error;
    }
  },

  async updateCarnet(id, updates) {
    try {
      const docRef = doc(db, CARNETS, id);
      await updateDoc(docRef, updates);
      return { id, ...updates };
    } catch (error) {
      console.error("Error updating carnet:", error);
      throw error;
    }
  },

  async deleteCarnet(id) {
    try {
      const docRef = doc(db, CARNETS, id);
      await updateDoc(docRef, { active: false });
    } catch (error) {
      console.error("Error deleting carnet:", error);
      throw error;
    }
  },
};

export default bookingService;